import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProblemReportModal = ({ isOpen, onClose }) => {
  const [problemText, setProblemText] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locations = [
    { id: 'nowysacz', name: 'Nowy Sącz', icon: '🏔️' },
    { id: 'krakow', name: 'Kraków', icon: '🏰' },
    { id: 'grodek', name: 'Gródek nad Dunajcem', icon: '🌊' }
  ];

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!problemText.trim()) {
      toast.error('Proszę opisać problem');
      return;
    }

    if (!selectedLocation) {
      toast.error('Proszę wybrać lokalizację');
      return;
    }

    setIsSubmitting(true);

    try {
      // Build a friendly title from the first sentence (max ~60 chars)
      const cleaned = problemText.trim();
      const firstLine = cleaned.split(/[.\n!?]/)[0].trim();
      const title = firstLine.length > 0 && firstLine.length <= 80
        ? firstLine
        : (cleaned.length > 60 ? cleaned.slice(0, 57) + '…' : cleaned);

      // Route to restaurant_matters so it appears in "Sprawy restauracji" tab
      // (same place as in-app FAB reports — single inbox for the manager)
      await axios.post(`${API}/api/restaurant-matters/matters`, {
        location: selectedLocation,
        title,
        description: cleaned,
        priority: 'medium',
        createdBy: 'anonymous_login_screen',
        createdByName: 'Anonim (ekran logowania)',
        photos: [],
      });

      toast.success('✅ Sprawa została zgłoszona!');
      toast.success('🙏 Manager zajmie się tym jak najszybciej!', {
        duration: 4000
      });

      setProblemText('');
      setSelectedLocation('');
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      console.error('Error submitting matter:', error);
      toast.error('Nie udało się zgłosić sprawy');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px'
      }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 100px rgba(239, 68, 68, 0.3)',
          border: '2px solid rgba(239, 68, 68, 0.3)'
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '800',
            color: '#fff',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            🐛 Zgłoś sprawę
          </h2>
          <p style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: '1.6'
          }}>
            Zauważyłeś coś co manager powinien wiedzieć? (np. usterka, konflikt, prośba, sugestia) Zgłoś anonimowo — sprawa trafi prosto do skrzynki managera.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Location Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '12px'
            }}>
              Wybierz lokalizację *
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '12px'
            }}>
              {locations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => setSelectedLocation(loc.id)}
                  style={{
                    padding: '14px',
                    borderRadius: '12px',
                    border: selectedLocation === loc.id 
                      ? '2px solid #ef4444' 
                      : '2px solid rgba(255, 255, 255, 0.1)',
                    background: selectedLocation === loc.id 
                      ? 'rgba(239, 68, 68, 0.2)' 
                      : 'rgba(255, 255, 255, 0.05)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{loc.icon}</span>
                  <span>{loc.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Problem Description */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '8px'
            }}>
              Opisz problem *
            </label>
            <textarea
              value={problemText}
              onChange={(e) => setProblemText(e.target.value)}
              placeholder="Np. 'Konflikty między kelnerami w sobotę wieczorem', 'Kłótnia kuchni z salą o zamówienia', 'Nieporozumienia w zespole'..."
              rows={5}
              style={{
                width: '100%',
                resize: 'vertical',
                minHeight: '120px',
                fontFamily: 'inherit',
                fontSize: '14px',
                lineHeight: '1.5',
                padding: '12px',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
              required
              disabled={isSubmitting}
            />
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.5)',
              marginTop: '6px',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>Bądź jak najbardziej szczegółowy</span>
              <span>{problemText.length} znaków</span>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'transparent',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
              disabled={isSubmitting}
            >
              Anuluj
            </button>
            <button
              type="submit"
              style={{
                padding: '12px 28px',
                fontSize: '14px',
                fontWeight: '700',
                borderRadius: '12px',
                border: 'none',
                background: isSubmitting 
                  ? 'rgba(239, 68, 68, 0.5)' 
                  : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#fff',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1,
                transition: 'all 0.2s',
                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.4)';
                }
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ Wysyłanie...' : '🚨 Zgłoś sprawę'}
            </button>
          </div>
        </form>

        <div style={{
          marginTop: '20px',
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '12px',
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.7)',
          lineHeight: '1.5',
          border: '1px solid rgba(239, 68, 68, 0.2)'
        }}>
          <strong>🔒 Anonimowość:</strong> Twoje zgłoszenie jest całkowicie anonimowe. Manager zobaczy tylko opis sprawy i lokalizację (bez Twoich danych).
        </div>
      </div>
    </div>
  );
};

export default ProblemReportModal;
